import { AuthBrandPanel } from '@/client/components/brand/AuthBrandPanel';
import { supabase } from '@/client/lib/supabase';
import { trpc } from '@/client/utils/trpc';
import { trpcVanilla } from '@/client/utils/trpc-client';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/onboarding')({
  beforeLoad: async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw redirect({ to: '/login' });

    const { needsOnboarding } = await trpcVanilla.user.needsOnboarding.query();
    if (!needsOnboarding) throw redirect({ to: '/' });
  },
  component: OnboardingPage,
});

function OnboardingPage() {
  const router = useRouter();
  const { data: adminCheck } = trpc.user.hasAdminPassword.useQuery();
  const [fullName, setFullName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const complete = trpc.user.completeOnboarding.useMutation({
    onSuccess: () => router.navigate({ to: '/' }),
  });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPasswordError('');

    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    const { error: pwError } = await supabase.auth.updateUser({ password });
    if (pwError) {
      setPasswordError(pwError.message);
      return;
    }

    complete.mutate({
      full_name: fullName,
      job_title: jobTitle.trim() || undefined,
      adminPassword: adminPassword || undefined,
    });
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Brand panel */}
      <AuthBrandPanel
        eyebrow="welcome"
        title="Let's get you set up."
        body="Just a few details and you'll be ready to go."
      />

      {/* Form panel */}
      <div className="flex flex-1 items-center justify-center p-10">
        <div className="flex w-full max-w-[360px] flex-col gap-[22px]">
          <div className="flex flex-col gap-1.5">
            <h1 className="m-0 text-[26px] font-semibold tracking-tight text-foreground">
              Your profile
            </h1>
            <p className="m-0 text-[13px] text-muted-foreground">
              Tell us a bit about yourself to continue.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="full-name" className="text-[13px] font-medium text-foreground">
                Full name <span className="text-red-500">*</span>
              </label>
              <input
                id="full-name"
                type="text"
                autoComplete="name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                placeholder="Léa Dupont"
                className="h-[42px] w-full rounded-lg border border-input bg-card px-3 text-[14px] text-foreground placeholder:text-muted-foreground/70 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="job-title" className="text-[13px] font-medium text-foreground">
                Job title <span className="font-normal text-muted-foreground">(optional)</span>
              </label>
              <input
                id="job-title"
                type="text"
                autoComplete="organization-title"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="Designer"
                className="h-[42px] w-full rounded-lg border border-input bg-card px-3 text-[14px] text-foreground placeholder:text-muted-foreground/70 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="password" className="text-[13px] font-medium text-foreground">
                Password <span className="text-red-500">*</span>
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="h-[42px] w-full rounded-lg border border-input bg-card px-3 text-[14px] text-foreground placeholder:text-muted-foreground/70 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="confirm-password" className="text-[13px] font-medium text-foreground">
                Confirm password <span className="text-red-500">*</span>
              </label>
              <input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="h-[42px] w-full rounded-lg border border-input bg-card px-3 text-[14px] text-foreground placeholder:text-muted-foreground/70 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {passwordError && (
              <p className="text-[13px] text-destructive" role="alert">
                {passwordError}
              </p>
            )}

            {adminCheck?.enabled && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="admin-password" className="text-[13px] font-medium text-foreground">
                  Admin password{' '}
                  <span className="font-normal text-muted-foreground">(optional)</span>
                </label>
                <input
                  id="admin-password"
                  type="password"
                  autoComplete="off"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-[42px] w-full rounded-lg border border-input bg-card px-3 text-[14px] text-foreground placeholder:text-muted-foreground/70 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            )}

            {complete.error && (
              <p className="text-[13px] text-destructive" role="alert">
                {complete.error.message}
              </p>
            )}

            <button
              type="submit"
              disabled={complete.isPending}
              className="mt-0.5 flex h-[42px] w-full cursor-pointer items-center justify-center rounded-lg bg-primary text-[15px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
            >
              {complete.isPending ? 'Saving…' : 'Continue'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
